import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  FileText, Clock, CheckCircle2, DollarSign, TrendingUp, Plus, ChevronRight, ShieldCheck, Lock, Zap,
} from "lucide-react";
import Navbar from "../components/Navbar";
import { BrandBadge } from "../components/CardBrand";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const StatusPill = ({ status }) => {
  const map = {
    pending: { bg: "#FFFBEB", fg: "#D97706", label: "Pending" },
    signed: { bg: "#ECFDF5", fg: "#059669", label: "Signed" },
  };
  const s = map[status] || map.pending;
  return (
    <span
      data-testid="authorization-form-row-status"
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
      style={{ background: s.bg, color: s.fg }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.fg }} />
      {s.label}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, accent, i }) => (
  <div className={`animate-rise delay-${i} rounded-xl border border-[#E2E8F0] bg-white p-5`}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">{label}</span>
      <span className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}14` }}>
        <Icon size={16} style={{ color: accent }} />
      </span>
    </div>
    <p className="mt-3 text-3xl font-extrabold tracking-tight text-[#0F172A] font-mono">{value}</p>
  </div>
);

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [forms, setForms] = useState([]);
  const [sub, setSub] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [s, f, sb] = await Promise.all([api.get("/stats"), api.get("/forms"), api.get("/subscription")]);
      setStats(s.data);
      setForms(f.data);
      setSub(sb.data);
    } catch (e) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const invite = localStorage.getItem("authvault_invite");
    if (!invite) { load(); return; }
    localStorage.removeItem("authvault_invite");
    api.post("/team/accept", { token: invite })
      .then(async (r) => { toast.success(`You joined ${r.data.vault_name}'s team`); await refreshUser(); })
      .catch((err) => toast.error(err?.response?.data?.detail || "Could not accept invitation"))
      .finally(load);
  }, []);

  const shown = forms.filter((f) => (filter === "all" ? true : f.status === filter));
  const money = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-5 py-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0F172A]">
              {sub?.vault_name ? sub.vault_name : user?.business_name ? user.business_name : "Your"} Authorizations
            </h1>
            <p className="text-sm text-[#64748B] mt-1">Create, send and store signed card authorization forms.</p>
          </div>
          <Link
            to="/create"
            data-testid="dashboard-create-form-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-[#0F172A] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1E293B] transition-all active:scale-[0.98]"
          >
            <Plus size={16} /> Create authorization
          </Link>
        </div>

        {sub && sub.subscription_status === "past_due" && (
          <div data-testid="past-due-banner" className="mt-6 rounded-xl border border-[#FECACA] bg-[#FEF2F2] p-4 flex items-center justify-between gap-4 flex-wrap">
            <p className="text-sm text-[#B91C1C]"><strong>Payment failed.</strong> Your last renewal didn't go through. Update your card to keep your {sub.plan} plan — otherwise your account will drop to Free when Stripe stops retrying.</p>
            <Link to="/billing" data-testid="past-due-fix-btn" className="inline-flex items-center gap-2 rounded-lg bg-[#DC2626] px-4 py-2 text-sm font-semibold text-white hover:bg-[#B91C1C] transition-colors">Fix payment</Link>
          </div>
        )}

        {sub && sub.limit && sub.used >= sub.limit && (
          <div data-testid="limit-reached-banner" className="mt-6 rounded-xl border border-[#FDE68A] bg-[#FFFBEB] p-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <span className="h-9 w-9 rounded-lg bg-[#FEF3C7] flex items-center justify-center shrink-0"><Lock size={16} className="text-[#D97706]" /></span>
              <div>
                <p className="font-semibold text-[#92400E]">You've reached your {sub.plan} plan limit ({sub.used}/{sub.limit})</p>
                <p className="text-sm text-[#B45309]">New forms can still be created, but sending &amp; signing are paused. Upgrade or refer a business to unlock more.</p>
              </div>
            </div>
            <Link to="/billing" data-testid="banner-upgrade-btn" className="inline-flex items-center gap-2 rounded-lg bg-[#D97706] px-4 py-2 text-sm font-semibold text-white hover:bg-[#B45309] transition-colors">
              <Zap size={15} /> Upgrade plan
            </Link>
          </div>
        )}

        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard i={1} icon={FileText} label="Total Forms" value={stats?.total ?? "—"} accent="#2563EB" />          <StatCard i={2} icon={Clock} label="Pending" value={stats?.pending ?? "—"} accent="#D97706" />
          <StatCard i={3} icon={CheckCircle2} label="Signed" value={stats?.signed ?? "—"} accent="#059669" />
          <StatCard i={4} icon={DollarSign} label="Auth Volume" value={stats ? money(stats.volume) : "—"} accent="#0284C7" />
        </div>

        <div className="mt-8 rounded-xl border border-[#E2E8F0] bg-white overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[#E2E8F0] flex-wrap">
            <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
              {[
                { k: "all", t: "All" },
                { k: "pending", t: "Pending" },
                { k: "signed", t: "Signed" },
              ].map((tab) => (
                <button
                  key={tab.k}
                  data-testid={`status-filter-${tab.k}`}
                  onClick={() => setFilter(tab.k)}
                  className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                    filter === tab.k ? "bg-white text-[#0F172A] shadow-sm" : "text-[#64748B]"
                  }`}
                >
                  {tab.t}
                </button>
              ))}
            </div>
            {stats && (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#059669]">
                <TrendingUp size={14} /> {stats.sign_rate}% sign rate
              </span>
            )}
          </div>

          {loading ? (
            <div className="p-16 flex justify-center">
              <div className="h-8 w-8 rounded-full border-2 border-[#E2E8F0] border-t-[#2563EB] animate-spin" />
            </div>
          ) : shown.length === 0 ? (
            <div className="p-16 text-center">
              <span className="mx-auto h-12 w-12 rounded-xl bg-[#EFF6FF] flex items-center justify-center">
                <ShieldCheck size={22} className="text-[#2563EB]" />
              </span>
              <p className="mt-4 font-semibold text-[#0F172A]">No authorizations yet</p>
              <p className="text-sm text-[#64748B] mt-1">Create your first card authorization form to get started.</p>
              <Link to="/create" className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white">
                <Plus size={16} /> Create form
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-[#E2E8F0]">
              <div className="hidden sm:grid grid-cols-12 gap-4 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#94A3B8] bg-slate-50/60">
                <div className="col-span-4">Customer</div>
                <div className="col-span-3">Merchant</div>
                <div className="col-span-2 text-right">Amount</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-1" />
              </div>
              {shown.map((f) => (
                <Link
                  key={f.form_id}
                  to={`/forms/${f.form_id}`}
                  data-testid={`form-row-${f.form_id}`}
                  className="grid grid-cols-12 gap-4 px-5 py-4 items-center hover:bg-slate-50/70 transition-colors group"
                >
                  <div className="col-span-12 sm:col-span-4">
                    <p className="font-semibold text-[#0F172A]">{f.customer_name}</p>
                    <p className="text-xs text-[#64748B]">{f.customer_email || "No email"}</p>
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <p className="text-sm text-[#334155]">{f.merchant_name}</p>
                    <p className="text-xs text-[#94A3B8] capitalize">{f.frequency}</p>
                  </div>
                  <div className="col-span-3 sm:col-span-2 sm:text-right">
                    <span className="font-mono font-semibold text-[#0F172A]">{money(f.amount)}</span>
                  </div>
                  <div className="col-span-3 sm:col-span-2 flex items-center gap-1.5">
                    <StatusPill status={f.status} />
                    {f.locked && (
                      <span data-testid="row-locked-badge" title="Locked — upgrade to send or collect signature" className="inline-flex items-center rounded-md bg-[#FFFBEB] px-1.5 py-1 text-[#D97706]"><Lock size={12} /></span>
                    )}
                  </div>
                  <div className="hidden sm:flex col-span-1 justify-end text-[#CBD5E1] group-hover:text-[#2563EB]">
                    <ChevronRight size={18} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
